/**
 * Who owns `html.pu` (the PictureUp brand tokens, see pictureup.css).
 *
 * PictureUp pages switch it on while they are mounted
 * (usePictureUpBrand in ArollShell.tsx), and index.html switches it on
 * before React mounts on a PictureUp address so the first frame is
 * already ink. That early switch has to be undone when the app ends up
 * somewhere else without ever mounting a PictureUp page (a Kilogram
 * login opening /aroll is sent straight to the CRM), or the CRM would
 * wear the PictureUp look. `releasePictureUpClass` does that, and is
 * called on every route change by App.tsx.
 *
 * Lives outside ArollShell.tsx so App.tsx can use it without pulling
 * the PictureUp chunk into the main bundle.
 */

/** Count of mounted PictureUp-branded pages, so a page handing over to
 *  the next (both mounted for a frame) never drops the class between. */
let puMounts = 0;

declare global {
  interface Window {
    /** Set by index.html when it forced `dark` over a light CRM theme. */
    __KG_PU_FORCED_DARK__?: boolean;
    /** Set by index.html when it renamed the tab and swapped its icon
     *  for a PictureUp address: what was there before. */
    __KG_PU_PREMOUNT__?: {
      title: string;
      icon: string | null;
      iconType: string | null;
      touch: string | null;
    };
  }
}

/** A PictureUp page mounted: switch the tokens on. */
export function holdPictureUpClass(): void {
  puMounts += 1;
  document.documentElement.classList.add('pu');
}

/** A PictureUp page unmounted: switch the tokens off once none is left. */
export function dropPictureUpClass(): void {
  puMounts -= 1;
  if (puMounts <= 0) {
    puMounts = 0;
    document.documentElement.classList.remove('pu');
  }
}

/** Is this address one index.html treats as PictureUp? */
export function isPictureUpPage(pathname: string): boolean {
  return pathname === '/aroll' || pathname.startsWith('/aroll/');
}

/**
 * On a route change: if we are off PictureUp and no PictureUp page is
 * mounted, undo index.html's early switch (the class, and a `dark` it
 * forced over a light CRM theme, and the tab name and icon it swapped).
 */
export function releasePictureUpClass(pathname: string): void {
  if (isPictureUpPage(pathname) || puMounts > 0) return;
  const root = document.documentElement;
  root.classList.remove('pu');
  if (window.__KG_PU_FORCED_DARK__) {
    window.__KG_PU_FORCED_DARK__ = false;
    root.classList.remove('dark');
  }
  const before = window.__KG_PU_PREMOUNT__;
  if (before) {
    window.__KG_PU_PREMOUNT__ = undefined;
    document.title = before.title;
    const icon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    const touch = document.querySelector<HTMLLinkElement>('link[rel="apple-touch-icon"]');
    if (icon && before.icon !== null) icon.setAttribute('href', before.icon);
    if (icon && before.iconType !== null) icon.setAttribute('type', before.iconType);
    if (touch && before.touch !== null) touch.setAttribute('href', before.touch);
  }
  // The server gives PictureUp addresses PictureUp's web manifest.
  document.querySelector('link[rel="manifest"][href*="/brand/pictureup/"]')?.remove();
}
